/**
 * MatchControl — Law 41/42 events screen.
 * Tabs: Substitutions | Warnings | Penalties | Conduct | Edit Log
 *
 * Each tab is a list + a small "add new" form. Wired to /api/matches/<id>/{kind}/.
 *
 * 2026-05-22: rebuilt the UUID-paste UX. Player and team fields are
 * dropdowns sourced from the match scorecard (both XIs + team names),
 * number fields are clamped to sensible bounds, and Edit-log diffs are
 * rendered human-readable instead of raw JSON. The submit buttons stay
 * disabled until every required field is set so the previous "alert on
 * click" feedback is gone.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  listSubstitutions, createSubstitution,
  listWarnings, createWarning,
  listPenalties, createPenalty,
  listConduct, createConduct,
  listBallEdits, deleteEvent,
} from "../../../api/ballbyball.api";
import { getMatchScorecard } from "../../../api/matches.api";
import { ChevronLeft, Plus, Trash2 } from "lucide-react";

const TABS = [
  { k: "subs",    l: "Substitutions" },
  { k: "warn",    l: "Warnings" },
  { k: "pen",     l: "Penalties" },
  { k: "conduct", l: "Conduct" },
  { k: "edits",   l: "Edit log" },
];

// Pull both teams + all distinct players from the scorecard so every
// dropdown in this file can resolve UUIDs to display names.
function useMatchRoster(matchId) {
  const [data, setData] = useState({ teams: [], players: [], byId: {}, loading: true });
  useEffect(() => {
    if (!matchId) return;
    let alive = true;
    getMatchScorecard(matchId)
      .then((res) => {
        if (!alive) return;
        const d = res?.data || {};
        const innings = d.innings || [];
        const teams = [];
        const teamSeen = new Set();
        const players = [];
        const playerSeen = new Set();
        for (const inn of innings) {
          const battingTeamId = inn.batting_team_id || inn.team_id;
          const bowlingTeamId = inn.bowling_team_id;
          if (battingTeamId && !teamSeen.has(battingTeamId)) {
            teamSeen.add(battingTeamId);
            teams.push({ id: battingTeamId, name: inn.batting_team_name || inn.team_name || "Team" });
          }
          if (bowlingTeamId && !teamSeen.has(bowlingTeamId)) {
            teamSeen.add(bowlingTeamId);
            teams.push({ id: bowlingTeamId, name: inn.bowling_team_name || "Team" });
          }
          for (const row of (inn.batting || [])) {
            if (row.player_id && !playerSeen.has(row.player_id)) {
              playerSeen.add(row.player_id);
              players.push({
                id: row.player_id,
                name: row.name || row.short_name || "Player",
                team_id: battingTeamId,
                team_name: inn.batting_team_name || inn.team_name || "",
              });
            }
          }
          for (const row of (inn.bowling || [])) {
            if (row.player_id && !playerSeen.has(row.player_id)) {
              playerSeen.add(row.player_id);
              players.push({
                id: row.player_id,
                name: row.name || row.short_name || "Player",
                team_id: bowlingTeamId,
                team_name: inn.bowling_team_name || "",
              });
            }
          }
        }
        const byId = {};
        teams.forEach((t) => { byId[t.id] = { kind: "team", ...t }; });
        players.forEach((p) => { byId[p.id] = { kind: "player", ...p }; });
        setData({ teams, players, byId, loading: false });
      })
      .catch(() => {
        if (alive) setData({ teams: [], players: [], byId: {}, loading: false });
      });
    return () => { alive = false; };
  }, [matchId]);
  return data;
}

export default function MatchControl() {
  const { id: matchId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("subs");
  const roster = useMatchRoster(matchId);

  return (
    <div className="p-4 max-w-5xl mx-auto">
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => navigate(`/cricket/matches/${matchId}/scorecard`)}
          className="text-text-secondary hover:text-text-primary flex items-center gap-1 text-sm"
        >
          <ChevronLeft className="w-4 h-4" /> Back to scorecard
        </button>
      </div>

      <h2 className="text-xl font-bold mb-1">Match control</h2>
      <p className="text-sm text-text-secondary mb-4">
        Law 24, 41 &amp; 42 events — substitutions, warnings, penalties, conduct,
        and the past-ball edit history. All events here update derived stats
        and appear on the scorecard timeline.
      </p>

      {roster.loading && (
        <div className="mb-3 text-[11px] text-text-secondary">Loading roster…</div>
      )}
      {!roster.loading && roster.teams.length === 0 && (
        <div className="mb-3 rounded border border-amber-500/40 bg-amber-500/10 text-amber-200 px-3 py-2 text-xs">
          ⚠ No teams / players resolved from this match's scorecard. Pickers
          below will be empty — set Playing XI first.
        </div>
      )}

      <div className="flex gap-1 mb-4 border-b border-border-default">
        {TABS.map(({ k, l }) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
              tab === k
                ? "border-button-primary text-text-primary font-medium"
                : "border-transparent text-text-secondary hover:text-text-primary"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === "subs"    && <Substitutions matchId={matchId} roster={roster} />}
      {tab === "warn"    && <Warnings      matchId={matchId} roster={roster} />}
      {tab === "pen"     && <Penalties     matchId={matchId} />}
      {tab === "conduct" && <Conduct       matchId={matchId} roster={roster} />}
      {tab === "edits"   && <EditLog       matchId={matchId} />}
    </div>
  );
}

/* ── Generic helpers ─────────────────────────────────────────────────────── */
function useEventList(loader) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const reload = async () => {
    setLoading(true); setErr("");
    try {
      const r = await loader();
      setRows(r.data?.results || []);
    } catch (e) {
      setErr(e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []); // eslint-disable-line
  return { rows, loading, err, reload };
}

function Card({ title, children }) {
  return (
    <div className="bg-bg-primary border border-border-default rounded-xl p-4">
      <h3 className="text-sm font-semibold mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, children, fullSpan }) {
  return (
    <label className={`flex flex-col gap-1 text-xs ${fullSpan ? "col-span-2" : ""}`}>
      <span className="text-text-secondary uppercase tracking-wider text-[10px]">{label}</span>
      {children}
    </label>
  );
}

function Empty({ msg }) {
  return <p className="text-sm text-text-secondary py-4 text-center">{msg}</p>;
}

// Player dropdown grouped by team — same dropdown shape used by every
// "Pick a player" field in this file. Allows team-grouped optgroups so
// the operator can scan one team's bench at a time.
function PlayerSelect({ value, onChange, players, placeholder = "— select player —", excludeId = null }) {
  const grouped = useMemo(() => {
    const m = new Map();
    for (const p of (players || [])) {
      if (excludeId && p.id === excludeId) continue;
      const teamLabel = p.team_name || "Unassigned";
      if (!m.has(teamLabel)) m.set(teamLabel, []);
      m.get(teamLabel).push(p);
    }
    for (const arr of m.values()) arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [players, excludeId]);
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="bg-bg-secondary px-2 py-1 rounded text-xs"
    >
      <option value="">{placeholder}</option>
      {grouped.map(([team, arr]) => (
        <optgroup key={team} label={team}>
          {arr.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

function TeamSelect({ value, onChange, teams, placeholder = "— select team —" }) {
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="bg-bg-secondary px-2 py-1 rounded text-xs"
    >
      <option value="">{placeholder}</option>
      {(teams || []).map((t) => (
        <option key={t.id} value={t.id}>{t.name}</option>
      ))}
    </select>
  );
}

// Clamp + cast to a non-negative integer with a max bound.
function clampInt(v, { min = 0, max = Infinity } = {}) {
  const n = Number(v);
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, Math.trunc(n)));
}

function resolveName(id, byId) {
  if (!id) return "—";
  const e = byId[id];
  return e ? e.name : `${String(id).slice(0, 8)}…`;
}

/* ── Substitutions ──────────────────────────────────────────────────────── */
function Substitutions({ matchId, roster }) {
  const { rows, loading, reload } = useEventList(() => listSubstitutions(matchId));
  const [form, setForm] = useState({ type: "FIELDER", player_out: "", player_in: "", reason: "" });
  const canSubmit = !!form.player_out && !!form.player_in && form.player_out !== form.player_in;

  const add = async () => {
    if (!canSubmit) return;
    await createSubstitution(matchId, form);
    setForm({ type: "FIELDER", player_out: "", player_in: "", reason: "" });
    reload();
  };

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Card title="Add substitution">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type">
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="bg-bg-secondary px-2 py-1 rounded">
              <option value="FIELDER">Sub fielder</option>
              <option value="CONCUSSION">Concussion sub</option>
              <option value="IMPACT">Impact player (IPL)</option>
              <option value="RUNNER">Runner</option>
            </select>
          </Field>
          <Field label="Reason">
            <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
              className="bg-bg-secondary px-2 py-1 rounded" placeholder="injury / strategy" />
          </Field>
          <Field label="Player out">
            <PlayerSelect
              value={form.player_out}
              onChange={(v) => setForm({ ...form, player_out: v })}
              players={roster.players}
              excludeId={form.player_in}
            />
          </Field>
          <Field label="Player in">
            <PlayerSelect
              value={form.player_in}
              onChange={(v) => setForm({ ...form, player_in: v })}
              players={roster.players}
              excludeId={form.player_out}
            />
          </Field>
        </div>
        <button
          onClick={add}
          disabled={!canSubmit}
          title={!canSubmit ? "Pick both player out and player in (must differ)" : ""}
          className="mt-3 w-full px-3 py-2 rounded-md bg-button-primary text-white text-sm flex items-center justify-center gap-1 disabled:opacity-40"
        >
          <Plus className="w-4 h-4" /> Add substitution
        </button>
      </Card>

      <Card title={`Existing substitutions (${rows.length})`}>
        {loading ? <Empty msg="Loading…" /> :
         rows.length === 0 ? <Empty msg="None recorded yet." /> :
         <ul className="space-y-2">
           {rows.map((r) => (
             <li key={r.id} className="bg-bg-secondary/40 rounded p-2 text-xs flex justify-between items-start">
               <div>
                 <span className="text-button-primary uppercase font-medium text-[10px]">{r.type}</span>
                 <p className="mt-1">
                   <span className="text-text-secondary">Out:</span> {r.player_out_name || resolveName(r.player_out, roster.byId)}
                   {" → "}
                   <span className="text-text-secondary">In:</span> {r.player_in_name || resolveName(r.player_in, roster.byId)}
                 </p>
                 {r.reason && <p className="text-text-secondary mt-0.5 italic">"{r.reason}"</p>}
               </div>
               <button onClick={async () => { await deleteEvent("substitution", r.id); reload(); }}
                 className="text-text-secondary hover:text-red-400">
                 <Trash2 className="w-3.5 h-3.5" />
               </button>
             </li>
           ))}
         </ul>}
      </Card>
    </div>
  );
}

/* ── Warnings ───────────────────────────────────────────────────────────── */
function Warnings({ matchId, roster }) {
  const { rows, loading, reload } = useEventList(() => listWarnings(matchId));
  const [form, setForm] = useState({ level: "FIRST", violation: "DANGEROUS_BOWLING", against_team: "", note: "" });
  const canSubmit = !!form.against_team;

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Card title="Issue warning (Law 41)">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Level">
            <select value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className="bg-bg-secondary px-2 py-1 rounded">
              <option value="FIRST">First warning</option>
              <option value="FINAL">Final warning</option>
            </select>
          </Field>
          <Field label="Violation">
            <select value={form.violation} onChange={(e) => setForm({ ...form, violation: e.target.value })} className="bg-bg-secondary px-2 py-1 rounded">
              <option value="DANGEROUS_BOWLING">Dangerous bowling</option>
              <option value="PROTECTED_AREA">Bowler running on protected area</option>
              <option value="PITCH_DAMAGE">Damaging pitch</option>
              <option value="TIME_WASTING">Time wasting</option>
              <option value="DISTRACTION">Distraction</option>
              <option value="SHORT_PITCHED">Short-pitched limit</option>
              <option value="OTHER">Other</option>
            </select>
          </Field>
          <Field label="Against team">
            <TeamSelect value={form.against_team} onChange={(v) => setForm({ ...form, against_team: v })} teams={roster.teams} />
          </Field>
          <Field label="Note">
            <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="bg-bg-secondary px-2 py-1 rounded" />
          </Field>
        </div>
        <button
          onClick={async () => {
            if (!canSubmit) return;
            await createWarning(matchId, form);
            setForm({ ...form, note: "" }); reload();
          }}
          disabled={!canSubmit}
          title={!canSubmit ? "Pick the team this warning is against" : ""}
          className="mt-3 w-full px-3 py-2 rounded-md bg-button-primary text-white text-sm flex items-center justify-center gap-1 disabled:opacity-40"
        >
          <Plus className="w-4 h-4" /> Issue warning
        </button>
      </Card>

      <Card title={`Issued warnings (${rows.length})`}>
        {loading ? <Empty msg="Loading…" /> :
         rows.length === 0 ? <Empty msg="None." /> :
         <ul className="space-y-2">
           {rows.map((r) => (
             <li key={r.id} className="bg-bg-secondary/40 rounded p-2 text-xs flex justify-between">
               <div>
                 <span className={`text-[10px] uppercase font-medium ${r.level === "FINAL" ? "text-red-400" : "text-amber-400"}`}>{r.level}</span>{" "}
                 <span className="text-text-primary">{r.violation.replaceAll("_", " ")}</span>
                 <span className="text-text-secondary"> · vs {resolveName(r.against_team, roster.byId)}</span>
                 {r.note && <p className="text-text-secondary italic mt-0.5">"{r.note}"</p>}
               </div>
               <button onClick={async () => { await deleteEvent("warning", r.id); reload(); }} className="text-text-secondary hover:text-red-400">
                 <Trash2 className="w-3.5 h-3.5" />
               </button>
             </li>
           ))}
         </ul>}
      </Card>
    </div>
  );
}

/* ── Penalties ──────────────────────────────────────────────────────────── */
function Penalties({ matchId }) {
  const { rows, loading, reload } = useEventList(() => listPenalties(matchId));
  const [form, setForm] = useState({ runs: 5, awarded_to: "BATTING", reason: "FAKE_FIELDING", note: "" });
  const canSubmit = form.runs > 0;

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Card title="Award penalty">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Runs (1–10)">
            <input
              type="number"
              min={1} max={10}
              value={form.runs}
              onChange={(e) => setForm({ ...form, runs: clampInt(e.target.value, { min: 1, max: 10 }) })}
              className="bg-bg-secondary px-2 py-1 rounded"
            />
          </Field>
          <Field label="Awarded to">
            <select value={form.awarded_to} onChange={(e) => setForm({ ...form, awarded_to: e.target.value })} className="bg-bg-secondary px-2 py-1 rounded">
              <option value="BATTING">Batting side</option>
              <option value="BOWLING">Bowling side</option>
            </select>
          </Field>
          <Field label="Reason" fullSpan>
            <select value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="bg-bg-secondary px-2 py-1 rounded">
              <option value="FAKE_FIELDING">Fake fielding</option>
              <option value="BALL_TAMPERING">Ball tampering</option>
              <option value="PITCH_DAMAGE">Damaging pitch</option>
              <option value="DELIBERATE_NB">Deliberate no-ball</option>
              <option value="STEALING_RUN">Stealing a run</option>
              <option value="DISTRACTION">Distraction</option>
              <option value="TIME_WASTING">Time wasting</option>
              <option value="OTHER">Other</option>
            </select>
          </Field>
          <Field label="Note" fullSpan>
            <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="bg-bg-secondary px-2 py-1 rounded" />
          </Field>
        </div>
        <button
          onClick={async () => { if (!canSubmit) return; await createPenalty(matchId, form); setForm({ ...form, note: "" }); reload(); }}
          disabled={!canSubmit}
          className="mt-3 w-full px-3 py-2 rounded-md bg-button-primary text-white text-sm flex items-center justify-center gap-1 disabled:opacity-40"
        >
          <Plus className="w-4 h-4" /> Award penalty
        </button>
      </Card>

      <Card title={`Awarded penalties (${rows.length})`}>
        {loading ? <Empty msg="Loading…" /> :
         rows.length === 0 ? <Empty msg="None." /> :
         <ul className="space-y-2">
           {rows.map((r) => (
             <li key={r.id} className="bg-bg-secondary/40 rounded p-2 text-xs flex justify-between">
               <div>
                 <span className="font-mono text-button-primary mr-1">+{r.runs}</span>
                 to <span className="font-medium">{r.awarded_to}</span>{" — "}
                 <span className="text-text-secondary">{r.reason.replaceAll("_", " ")}</span>
                 {r.note && <p className="text-text-secondary italic mt-0.5">"{r.note}"</p>}
               </div>
               <button onClick={async () => { await deleteEvent("penalty", r.id); reload(); }} className="text-text-secondary hover:text-red-400">
                 <Trash2 className="w-3.5 h-3.5" />
               </button>
             </li>
           ))}
         </ul>}
      </Card>
    </div>
  );
}

/* ── Conduct reports (Law 42) ───────────────────────────────────────────── */
function Conduct({ matchId, roster }) {
  const { rows, loading, reload } = useEventList(() => listConduct(matchId));
  const [form, setForm] = useState({ player: "", level: "L1", offence: "", demerit_points: 1, fine_pct: 0, note: "" });
  const canSubmit = !!form.player && !!form.offence.trim();

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Card title="File conduct report">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Player" fullSpan>
            <PlayerSelect value={form.player} onChange={(v) => setForm({ ...form, player: v })} players={roster.players} />
          </Field>
          <Field label="Level">
            <select value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className="bg-bg-secondary px-2 py-1 rounded">
              <option value="L1">Level 1</option>
              <option value="L2">Level 2</option>
              <option value="L3">Level 3</option>
              <option value="L4">Level 4</option>
            </select>
          </Field>
          <Field label="Demerit points (0–8)">
            <input
              type="number" min={0} max={8}
              value={form.demerit_points}
              onChange={(e) => setForm({ ...form, demerit_points: clampInt(e.target.value, { min: 0, max: 8 }) })}
              className="bg-bg-secondary px-2 py-1 rounded"
            />
          </Field>
          <Field label="Fine % (0–100)">
            <input
              type="number" min={0} max={100}
              value={form.fine_pct}
              onChange={(e) => setForm({ ...form, fine_pct: clampInt(e.target.value, { min: 0, max: 100 }) })}
              className="bg-bg-secondary px-2 py-1 rounded"
            />
          </Field>
          <Field label="Offence" fullSpan>
            <input value={form.offence} onChange={(e) => setForm({ ...form, offence: e.target.value })} className="bg-bg-secondary px-2 py-1 rounded" placeholder="e.g. dissent toward umpire" />
          </Field>
          <Field label="Note" fullSpan>
            <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="bg-bg-secondary px-2 py-1 rounded" rows={2} />
          </Field>
        </div>
        <button
          onClick={async () => {
            if (!canSubmit) return;
            await createConduct(matchId, form);
            setForm({ ...form, offence: "", note: "" }); reload();
          }}
          disabled={!canSubmit}
          title={!canSubmit ? "Pick a player and describe the offence" : ""}
          className="mt-3 w-full px-3 py-2 rounded-md bg-button-primary text-white text-sm flex items-center justify-center gap-1 disabled:opacity-40"
        >
          <Plus className="w-4 h-4" /> File report
        </button>
      </Card>

      <Card title={`Filed reports (${rows.length})`}>
        {loading ? <Empty msg="Loading…" /> :
         rows.length === 0 ? <Empty msg="None." /> :
         <ul className="space-y-2">
           {rows.map((r) => (
             <li key={r.id} className="bg-bg-secondary/40 rounded p-2 text-xs flex justify-between">
               <div>
                 <span className="font-medium">{r.level}</span>{" — "}
                 <span className="text-text-secondary">{r.offence}</span>{" "}
                 <span className="text-button-primary font-mono">({r.demerit_points} dp)</span>
                 {r.fine_pct ? <span className="text-amber-400 text-[10px] ml-1">fine {r.fine_pct}%</span> : null}
                 <p className="text-text-secondary mt-0.5">Player: {r.player_name || resolveName(r.player, roster.byId)}</p>
               </div>
               <button onClick={async () => { await deleteEvent("conduct", r.id); reload(); }} className="text-text-secondary hover:text-red-400">
                 <Trash2 className="w-3.5 h-3.5" />
               </button>
             </li>
           ))}
         </ul>}
      </Card>
    </div>
  );
}

/* ── Edit log (read-only) ───────────────────────────────────────────────── */
// Render diff entries as "field: old → new" lines instead of pretty-printed
// JSON. Each backend diff is shape {key: {old, new}} or just {key: new};
// we walk shallowly and format what we recognise. Anything unrecognised
// falls back to JSON so we never silently lose information.
function humanizeDiff(diff) {
  if (!diff || typeof diff !== "object") return [];
  // Some diffs are wrapped as {new: {...}, old: {...}} pairs at the top level.
  const newObj = diff.new && typeof diff.new === "object" ? diff.new : null;
  const oldObj = diff.old && typeof diff.old === "object" ? diff.old : null;
  if (newObj) {
    const out = [];
    for (const [k, v] of Object.entries(newObj)) {
      const before = oldObj ? oldObj[k] : undefined;
      out.push({ key: k, before, after: v });
    }
    return out;
  }
  return Object.entries(diff).map(([k, v]) => ({
    key: k,
    before: v && typeof v === "object" && "old" in v ? v.old : undefined,
    after:  v && typeof v === "object" && "new" in v ? v.new : v,
  }));
}

function formatVal(v) {
  if (v === undefined) return "—";
  if (v === null) return "null";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function EditLog({ matchId }) {
  const { rows, loading } = useEventList(() => listBallEdits(matchId));
  return (
    <Card title={`Past-ball edits (${rows.length})`}>
      <p className="text-[11px] text-text-secondary mb-3">
        Audit trail — every PATCH on a ball writes an entry here. Field
        changes show as `field: before → after`.
      </p>
      {loading ? <Empty msg="Loading…" /> :
       rows.length === 0 ? <Empty msg="No edits yet." /> :
       <ul className="space-y-2">
         {rows.map((r) => {
           const changes = humanizeDiff(r.diff);
           return (
             <li key={r.id} className="bg-bg-secondary/40 rounded p-2 text-xs">
               <div className="flex justify-between text-[10px] text-text-secondary">
                 <span className="font-mono">{r.ball_id.slice(0, 8)}…</span>
                 <span>by {r.edited_by_name || r.edited_by.slice(0, 8)} · {new Date(r.created_at).toLocaleString()}</span>
               </div>
               {changes.length === 0 ? (
                 <p className="mt-1 text-text-secondary italic">(no field-level diff captured)</p>
               ) : (
                 <ul className="mt-1 space-y-0.5">
                   {changes.map((c, i) => (
                     <li key={i} className="font-mono text-[11px]">
                       <span className="text-text-secondary">{c.key}:</span>{" "}
                       {c.before === undefined
                         ? <span className="text-emerald-300">{formatVal(c.after)}</span>
                         : (
                           <>
                             <span className="text-rose-300">{formatVal(c.before)}</span>
                             <span className="text-text-secondary"> → </span>
                             <span className="text-emerald-300">{formatVal(c.after)}</span>
                           </>
                         )}
                     </li>
                   ))}
                 </ul>
               )}
             </li>
           );
         })}
       </ul>}
    </Card>
  );
}
